import { EventBus } from './Events';

/**
 * Ayarlar: kalıcı, sürümlü ve bozuk veriye dayanıklı.
 * Grafik kalitesi somut değerlere (pixelRatio, gölge, render mesafesi) çevrilir;
 * böylece cihaz gücüne göre tek yerden ölçeklenir.
 */

export type QualityLevel = 'low' | 'medium' | 'high' | 'ultra';
export type ControlScheme = 'auto' | 'touch' | 'desktop';

export interface GraphicsSettings {
  quality: QualityLevel;
  /** Chunk cinsinden görüş mesafesi. */
  renderDistance: number;
  shadows: boolean;
  particles: number; // 0..1 yoğunluk
  fov: number;
  targetFps: 30 | 60 | 120;
}

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  ambient: number;
}

export interface ControlSettings {
  scheme: ControlScheme;
  cameraSensitivity: number;
  touchSensitivity: number;
  buttonScale: number;
  /** Mobil buton düzeni: sağ el / sol el. */
  layout: 'right' | 'left';
  invertY: boolean;
}

export interface SettingsData {
  version: number;
  graphics: GraphicsSettings;
  audio: AudioSettings;
  controls: ControlSettings;
}

export interface QualityProfile {
  pixelRatio: number;
  shadows: boolean;
  shadowMapSize: number;
  renderDistance: number;
  particles: number;
  terrainSegments: number;
  vegetationDensity: number;
  fogDensityScale: number;
}

const SETTINGS_VERSION = 1;
const STORAGE_KEY = 'kg.settings';

export const QUALITY_PROFILES: Readonly<Record<QualityLevel, QualityProfile>> = Object.freeze({
  low: {
    pixelRatio: 0.7,
    shadows: false,
    shadowMapSize: 512,
    renderDistance: 3,
    particles: 0.25,
    terrainSegments: 8,
    vegetationDensity: 0.45,
    fogDensityScale: 1.35,
  },
  medium: {
    pixelRatio: 1,
    shadows: true,
    shadowMapSize: 1024,
    renderDistance: 4,
    particles: 0.6,
    terrainSegments: 12,
    vegetationDensity: 0.7,
    fogDensityScale: 1.1,
  },
  high: {
    pixelRatio: 1.5,
    shadows: true,
    shadowMapSize: 2048,
    renderDistance: 6,
    particles: 1,
    terrainSegments: 16,
    vegetationDensity: 1,
    fogDensityScale: 1,
  },
  ultra: {
    pixelRatio: 2,
    shadows: true,
    shadowMapSize: 2048,
    renderDistance: 8,
    particles: 1,
    terrainSegments: 24,
    vegetationDensity: 1.2,
    fogDensityScale: 0.9,
  },
});

function defaults(): SettingsData {
  // Mobilde varsayılan daha muhafazakâr başlar.
  const touch = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  const quality: QualityLevel = touch ? 'low' : 'medium';
  const profile = QUALITY_PROFILES[quality];
  return {
    version: SETTINGS_VERSION,
    graphics: {
      quality,
      renderDistance: profile.renderDistance,
      shadows: profile.shadows,
      particles: profile.particles,
      fov: 75,
      targetFps: 60,
    },
    audio: { master: 0.8, music: 0.5, sfx: 0.9, ambient: 0.6 },
    controls: {
      scheme: 'auto',
      cameraSensitivity: 1,
      touchSensitivity: 1,
      buttonScale: 1,
      layout: 'right',
      invertY: false,
    },
  };
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

function num(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? clamp(n, min, max) : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/** Kaydedilmiş veriyi şemaya zorla; eksik/bozuk alanlar varsayılana döner. */
function sanitize(raw: unknown): SettingsData {
  const d = defaults();
  if (!raw || typeof raw !== 'object') return d;
  const r = raw as Record<string, any>;
  const g = (r.graphics ?? {}) as Record<string, unknown>;
  const a = (r.audio ?? {}) as Record<string, unknown>;
  const c = (r.controls ?? {}) as Record<string, unknown>;
  return {
    version: SETTINGS_VERSION,
    graphics: {
      quality: pick(g.quality, ['low', 'medium', 'high', 'ultra'] as const, d.graphics.quality),
      renderDistance: Math.round(num(g.renderDistance, d.graphics.renderDistance, 2, 12)),
      shadows: bool(g.shadows, d.graphics.shadows),
      particles: num(g.particles, d.graphics.particles, 0, 1),
      fov: Math.round(num(g.fov, d.graphics.fov, 50, 120)),
      targetFps: (([30, 60, 120] as const).includes(g.targetFps as 30) ? g.targetFps : d.graphics.targetFps) as 30 | 60 | 120,
    },
    audio: {
      master: num(a.master, d.audio.master, 0, 1),
      music: num(a.music, d.audio.music, 0, 1),
      sfx: num(a.sfx, d.audio.sfx, 0, 1),
      ambient: num(a.ambient, d.audio.ambient, 0, 1),
    },
    controls: {
      scheme: pick(c.scheme, ['auto', 'touch', 'desktop'] as const, d.controls.scheme),
      cameraSensitivity: num(c.cameraSensitivity, d.controls.cameraSensitivity, 0.2, 3),
      touchSensitivity: num(c.touchSensitivity, d.controls.touchSensitivity, 0.2, 3),
      buttonScale: num(c.buttonScale, d.controls.buttonScale, 0.7, 1.6),
      layout: pick(c.layout, ['right', 'left'] as const, d.controls.layout),
      invertY: bool(c.invertY, d.controls.invertY),
    },
  };
}

type SettingsEvents = {
  changed: SettingsData;
  'graphics:changed': GraphicsSettings;
  'audio:changed': AudioSettings;
  'controls:changed': ControlSettings;
};

export class Settings {
  readonly events = new EventBus<SettingsEvents>();
  private data: SettingsData;

  constructor() {
    this.data = sanitize(this.read());
  }

  private read(): unknown {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn('[Settings] okunamadı, varsayılanlar kullanılıyor:', err);
      return null;
    }
  }

  get graphics(): Readonly<GraphicsSettings> {
    return this.data.graphics;
  }
  get audio(): Readonly<AudioSettings> {
    return this.data.audio;
  }
  get controls(): Readonly<ControlSettings> {
    return this.data.controls;
  }
  get all(): Readonly<SettingsData> {
    return this.data;
  }

  /** Aktif kalite profili; renderDistance kullanıcı ayarıyla ezilir. */
  get profile(): QualityProfile {
    const base = QUALITY_PROFILES[this.data.graphics.quality];
    return {
      ...base,
      shadows: base.shadows && this.data.graphics.shadows,
      renderDistance: this.data.graphics.renderDistance,
      particles: Math.min(base.particles, this.data.graphics.particles),
    };
  }

  /** Dokunmatik mi? 'auto' ise cihazdan tespit edilir. */
  get isTouch(): boolean {
    const s = this.data.controls.scheme;
    if (s === 'touch') return true;
    if (s === 'desktop') return false;
    return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }

  patchGraphics(patch: Partial<GraphicsSettings>): void {
    // Kalite değişince o profilin makul varsayılanlarını da uygula.
    if (patch.quality && patch.quality !== this.data.graphics.quality) {
      const p = QUALITY_PROFILES[patch.quality];
      patch = { renderDistance: p.renderDistance, shadows: p.shadows, particles: p.particles, ...patch };
    }
    this.data = sanitize({ ...this.data, graphics: { ...this.data.graphics, ...patch } });
    this.persist();
    this.events.emit('graphics:changed', this.data.graphics);
    this.events.emit('changed', this.data);
  }

  patchAudio(patch: Partial<AudioSettings>): void {
    this.data = sanitize({ ...this.data, audio: { ...this.data.audio, ...patch } });
    this.persist();
    this.events.emit('audio:changed', this.data.audio);
    this.events.emit('changed', this.data);
  }

  patchControls(patch: Partial<ControlSettings>): void {
    this.data = sanitize({ ...this.data, controls: { ...this.data.controls, ...patch } });
    this.persist();
    this.events.emit('controls:changed', this.data.controls);
    this.events.emit('changed', this.data);
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch (err) {
      console.warn('[Settings] kaydedilemedi:', err);
    }
  }
}
